// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getErrorMessages, getSumQtyPerUom } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityMessageType,
    ProductSettingsModel,
    ProductSubscriptionDto,
    PromotionModel,
} from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CartLineContainer, {
    CartLineContainerStyles,
    MobileCartLineContainerStyles,
    mobileCartLineContainerStyles,
} from "@keller/App/Components/Cart/CartContainerLayout";
import Notify from "@keller/App/Components/Notify";
import cartLineSharedStyles from "@keller/App/SharedStyle/Cart/CartLineSharedStyles";
import { xPartNumberStyle } from "@keller/App/SharedStyle/ProductPartNumberStyle";
import ProductAvailability from "@keller/Overrides/Components/ProductAvailability";
import ProductPrice, { ProductPriceStyles } from "@keller/Overrides/Components/ProductPrice";
import CartLineQuantity, { CartLineQuantityStyles } from "@keller/Overrides/Widgets/Cart/CartLineQuantity";
import { hideElementOnPrint, mergeWithCss, parseBoolean, xScreen } from "@keller/XDL/Utilities";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    productSettings?: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine?: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine?: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
    // XNG-Changes
    editable?: boolean;
    showInvalidPriceMessage?: boolean;
    showSavingsPercent?: boolean;
    showSavingsAmount?: boolean;
    editableCostCode?: boolean;
    className?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    getRealTimeWarehouseInventory,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    // XNG-Changes
    cartLineContainerStyles?: CartLineContainerStyles;
    removeCartLineLink?: LinkPresentationProps;
    configurationOptionLabel?: TypographyProps;
    promotionsContainer?: InjectableCss;
    promotionNameLabel?: TypographyProps;
    mobileExtendedStyles?: MobileCartLineContainerStyles;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    productImage: {
        ...cartLineSharedStyles.productImage,
    },
    productBrand: {
        ...cartLineSharedStyles.productBrand,
    },
    productDescription: {
        ...cartLineSharedStyles.productDescription,
    },
    productPartNumbers: {
        ...cartLineSharedStyles.productPartNumbers,
    },
    configurationGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 16px;
            & > div:not(:last-child) {
                margin-bottom: 5px;
            }
        `,
    },
    configurationOptionLabel: {
        ...xPartNumberStyle.label,
    },
    configurationOptionText: {
        ...xPartNumberStyle.value,
    },
    promotionsContainer: {
        ...cartLineSharedStyles.promotionsContainer,
    },
    promotionNameLabel: {
        ...cartLineSharedStyles.promotionNameLabel,
    },
    promotionNameText: {
        ...cartLineSharedStyles.promotionNameText,
    },
    cartLineErrorMessageGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: 10px;
            margin-bottom: 16px;
            & > ${TypographyStyle}:not(:last-child) {
                margin-bottom: 10px;
            }
        `,
    },
    deliveryScheduleButton: {
        css: css`
            align-self: baseline;
        `,
    },
    costCodeGridItem: {
        ...cartLineSharedStyles.costCodeGridItem,
    },
    costCodeLabelText: {
        ...cartLineSharedStyles.costCodeLabelText,
    },
    costCodeText: {
        ...cartLineSharedStyles.costCodeText,
    },
    costCodeSelect: {
        cssOverrides: {
            formField: css`
                max-width: 200px;
            `,
        },
    },
    addToListLink: {
        ...cartLineSharedStyles.addToListLink,
    },
    removeCartLineLink: {
        ...cartLineSharedStyles.removeCartLineLink,
    },
    productPrice: {
        ...cartLineSharedStyles.productPrice,
    },
    extendedUnitNetPriceText: {
        ...cartLineSharedStyles.extendedUnitNetPriceText,
    },
    binInformationsGridItem: {
        width: 12,
    },
    // XNG-Changes
    mobileExtendedStyles: {
        peripheralActionsLeft: {
            ...mergeWithCss(mobileCartLineContainerStyles.peripheralActionsLeft, {
                css: css`
                    &&&& {
                        &,
                        & > span {
                            flex-direction: row;
                            ${xScreen("lte", "xs")} {
                                flex-direction: column;
                            }
                        }
                    }
                `,
            }),
        },
    },
    // xng-change
    productAvailability: {
        container: {
            css: css`
                ${hideElementOnPrint}
            `,
        },
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = true,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showAddToList = true,
    showProductNumber = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showPricing,
    showVmiBinDetails,
    // XNG-Changes
    editable = true,
    showSavingsAmount,
    showSavingsPercent,
    showInvalidPriceMessage,
    editableCostCode,
    className,
    getRealTimeWarehouseInventory,
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);

    // XNG-Chnages
    const [styles] = React.useState(() => mergeWithCss(cartLineCardExpandedStyles, extendedStyles));

    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine?.({ cartLineId: cartLine.id });
            } else {
                updateCartLine?.({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine?.({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine?.({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine?.({ cartLineId: cartLine.id });
        setShowConfirmationModal(false);
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine?.({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const errorMessages = getErrorMessages(cart, cartLine, showInventoryAvailability);
    // XNG-Changes: add new condition for cartLine.qtyOnHand < cartLine.qtyOrdered
    if (
        !(showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) &&
        cartLine.qtyOnHand < (cartLine.qtyOrdered || 0) &&
        (cartLine.availability?.messageType === AvailabilityMessageType.Available ||
            cartLine.availability?.messageType === AvailabilityMessageType.LowStock)
    ) {
        errorMessages.push(
            siteMessage(
                "Cart_ToManyQtyRequested",
                cartLine.qtyOnHand.toLocaleString(),
                getSumQtyPerUom(cart, cartLine).toLocaleString(),
            ),
        );
    }
    // XNG-Changes: added mobileExtendedStyle
    return (
        <>
            <CartLineContainer
                className={className}
                data-test-selector={`cartline_expanded1_${cartLine.productId}_${cartLine.unitOfMeasure}`}
                extendedStyles={styles.cartLineContainerStyles}
                mobileExtendedStyles={styles.mobileExtendedStyles}
            >
                <CartLineContainer.Image>
                    {showImage && <ProductImage product={cartLine} extendedStyles={styles.productImage} />}
                </CartLineContainer.Image>
                <CartLineContainer.Info>
                    {showBrand && cartLine.brand && (
                        <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                    )}
                    {showProductTitle && (
                        <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                    )}
                    <ProductPartNumbers
                        productNumber={cartLine.erpNumber}
                        customerProductNumber={cartLine.customerName}
                        manufacturerItem={cartLine.manufacturerItem}
                        extendedStyles={styles.productPartNumbers}
                        showProductNumber={showProductNumber}
                    />
                    {showVmiBinDetails && currentMode === "Vmi" && (
                        // XNG-Changes - Removed GridItem
                        // <GridItem {...styles.binInformationsGridItem}>
                        <ProductBinNumbers
                            locationId={cart.vmiLocationId}
                            binId={cartLine.vmiBinId}
                            isCondensed={false}
                            extendedStyles={styles.productBinNumbers}
                        />
                        // </GridItem>
                    )}
                    {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                        <StyledWrapper {...styles.configurationGridItem}>
                            {cartLine.sectionOptions!.map(option => (
                                <StyledWrapper key={option.sectionOptionId}>
                                    <Typography {...styles.configurationOptionLabel}>{option.sectionName}</Typography>
                                    <Typography {...styles.configurationOptionText}>{option.optionName}</Typography>
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    )}
                    {showPricing && promotions && promotions.length > 0 && (
                        <StyledWrapper {...styles.promotionsContainer}>
                            {promotions.map((promotion, index) => (
                                <StyledWrapper key={promotion.id}>
                                    <Typography {...styles.promotionNameLabel}>
                                        {`${translate("Promotion")} #${index + 1}:`}
                                    </Typography>
                                    <Typography {...styles.promotionNameText}>{promotion.name}</Typography>
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    )}
                    {errorMessages.length > 0 && (
                        <StyledWrapper {...styles.cartLineErrorMessageGridItem}>
                            {errorMessages.map((message, index) => (
                                <Notify
                                    {...styles.cartLineErrorMessageText}
                                    key={index}
                                    data-test-selector="cartline_errorMessage"
                                    type="danger"
                                >
                                    {message}
                                </Notify>
                            ))}
                        </StyledWrapper>
                    )}
                    {cartLine.isSubscription && cartLine.productSubscription && (
                        <ProductDeliveryScheduleButton
                            subscription={
                                cartLine.properties.productSubscription
                                    ? JSON.parse(cartLine.properties.productSubscription)
                                    : cartLine.productSubscription
                            }
                            disabled={
                                !cart.canModifyOrder ||
                                cartLine.isPromotionItem ||
                                !cartLine.qtyOrdered ||
                                cartLine.qtyOrdered <= 0
                            }
                            onSave={saveScheduleHandler}
                            extendedStyles={styles.deliveryScheduleButton}
                        />
                    )}
                    {cart.showCostCode && !cartLine.isPromotionItem && (
                        <StyledWrapper {...styles.costCodeGridItem}>
                            <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                            {cart.canEditCostCode ? (
                                <Select
                                    value={cartLine.costCode}
                                    onChange={costCodeChangeHandler}
                                    aria-label={translate("Cost Code")}
                                    {...styles.costCodeSelect}
                                >
                                    {cart.costCodes?.map(costCode => (
                                        <option key={costCode.costCode} value={costCode.costCode}>
                                            {costCode.description}
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                            )}
                        </StyledWrapper>
                    )}
                    {!editableCostCode && cart.showCostCode && cartLine.costCode && (
                        <StyledWrapper {...styles.costCodeGridItem} className="readonly">
                            <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                            <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                        </StyledWrapper>
                    )}
                </CartLineContainer.Info>
                <CartLineContainer.Qty>
                    <CartLineQuantity
                        cart={cart}
                        editable={editable}
                        showLabel={true}
                        onQtyOrderedChange={qtyOrderedChangeHandler}
                        extendedStyles={styles.quantity}
                    />
                </CartLineContainer.Qty>
                <CartLineContainer.Price>
                    {!cart.cartNotPriced && (
                        <ProductPrice
                            product={cartLine}
                            currencySymbol={cart.currencySymbol}
                            showSavings={false}
                            label={translate("Price/Unit")}
                            showSavingsAmount={showSavingsAmount}
                            showSavingsPercent={showSavingsPercent}
                            showInvalidPriceMessage={showInvalidPriceMessage}
                            extendedStyles={styles.productPrice}
                            showLabel={showPriceLabel}
                            showUnitOfMeasure={showPerUnitPrice}
                        />
                    )}
                </CartLineContainer.Price>
                <CartLineContainer.Total>
                    {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && showPricing && (
                        <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                            <SmallHeadingAndText
                                {...styles.extendedUnitNetPriceText}
                                data-test-selector="cartline_extendedUnitNetPrice"
                                heading={translate("Total")}
                                text={
                                    enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                        : cartLine.pricing.extendedUnitNetPriceDisplay
                                }
                            />
                            {enableVat && (
                                <>
                                    <Typography as="p" {...styles.vatLabelText}>
                                        {vatPriceDisplay === "DisplayWithVat" ||
                                        vatPriceDisplay === "DisplayWithAndWithoutVat"
                                            ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                            : translate("Ex. VAT")}
                                    </Typography>
                                    {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                        <>
                                            <Typography {...styles.subtotalWithoutVatText}>
                                                {cartLine.pricing.extendedUnitNetPriceDisplay}
                                            </Typography>
                                            <Typography as="p" {...styles.vatLabelText}>
                                                {translate("Ex. VAT")}
                                            </Typography>
                                        </>
                                    )}
                                </>
                            )}
                        </StyledWrapper>
                    )}
                </CartLineContainer.Total>
                <CartLineContainer.PeripheralActionsLeft>
                    {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                        <CartLineNotes
                            cart={cart}
                            editable={editable}
                            onNotesChange={notesChangeHandler}
                            extendedStyles={styles.cartLineNotes}
                        />
                    )}
                    {showAddToList && !hideAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && (
                        <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                            {translate("Add to Wish ist")}
                        </Link>
                    )}
                </CartLineContainer.PeripheralActionsLeft>
                <CartLineContainer.PeripheralAvailabilityAction>
                    {showInventoryAvailability && !cartLine.quoteRequired && (
                        <ProductAvailability
                            productId={cartLine.productId!}
                            availability={cartLine.availability!}
                            unitOfMeasure={cartLine.unitOfMeasure}
                            trackInventory={cartLine.trackInventory}
                            failedToLoadInventory={cart.failedToGetRealTimeInventory}
                            extendedStyles={styles.productAvailability}
                            // XNG-Changes: check for hasAvailabilityInOtherWarehouses - KSC-905
                            hasAvailabilityInOtherWarehouses={parseBoolean(
                                cartLine.properties.hasAvailabilityInOtherWarehouses,
                            )}
                        />
                    )}
                </CartLineContainer.PeripheralAvailabilityAction>
                <CartLineContainer.PeripheralRemoveAction>
                    {showRemoveAction ? (
                        <Link
                            onClick={() => setShowConfirmationModal(true)}
                            {...styles.removeCartLineLink}
                            data-test-selector="cartline_removeLine"
                        >
                            <VisuallyHidden>{translate("Remove")}</VisuallyHidden>
                            {translate("Remove")}
                        </Link>
                    ) : (
                        <StyledWrapper style={{ width: "60px" }}></StyledWrapper>
                    )}
                </CartLineContainer.PeripheralRemoveAction>
            </CartLineContainer>
            <TwoButtonModal
                headlineText={translate("Confirm Line Removal")}
                modalIsOpen={showConfirmationModal}
                messageText={siteMessage("Keller_Cart_RemoveSingleCartLine")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={() => setShowConfirmationModal(false)}
                onSubmit={() => removeCartLineClickHandler()}
                submitTestSelector="RemoveSingleCartLine"
                dataTestSelector="twoButtonModal_RemoveSingleCartLine"
            ></TwoButtonModal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
